
import { Fractionalizer1155ABI } from "../constants/ABI/Fractionalizer1155ABI";
import { Token, tokens, ShellToken, shellTokens, isShellToken, tokenMap, isDefaultShellToken, nftCollections, isNFTCollection, lbpTokens, externalLPTokens, externalTokens } from "../placeholders/tokens";
import { ContractCallContext, ContractCallResults, Multicall } from "ethereum-multicall";
import { BigNumber, BigNumberish } from "ethers";
import { calculateWrappedTokenId } from "./ocean/utils";
import { Zero } from '@ethersproject/constants'
import { Interaction } from "./ocean/types";
import { extract1155Data, multicall } from "./nftHelpers";
import { LBPPoolABI } from "@/constants/ABI/LBPPoolABI";
import { LiquidityPoolABI } from "@/constants/ABI/LiquidityPoolABI";
import { OLD_OCEAN_ADDRESS } from "@/constants/addresses";
import { createTokenLogo, tokenLogos } from "@/constants/tokenLogos";
export interface Edge {
    token: Token
    pool: string
    action: string
}

export const getTokenID = (token: Token | ShellToken): string => {
    return isShellToken(token) ? token.name : token.symbol
}

export type InteractionCallback = (amount: BigNumberish, direction: boolean, slippage?: string, nftID?: string) => Interaction

const LiquidityVertices = Object.keys(tokenMap)
const LiquidityVerticesMap = LiquidityVertices.reduce(
    (acc, next) => ({ ...acc, [next]: null }),
    {} as Record<string, null>,
);

export type LiquidityVertex = typeof LiquidityVertices[number];
export const isLiquidityVertex = (value: string): value is LiquidityVertex => {
    return value in LiquidityVerticesMap;
}
export type InteractionNetwork = {
    [LV in LiquidityVertex]: {
        [LV in LiquidityVertex]?: InteractionCallback
    }
}
export class LiquidityGraph {

    graph: Record<string, Edge[]> = {};

    constructor() {
        // multicall = new Multicall({ nodeUrl: `https://arbitrum-goerli.infura.io/v3/${infuraId}`, tryAggregate: true });

        const nftAddresses = new Set(nftCollections.map((collection) => collection.address))

        tokens.forEach((token) => {
            if(nftAddresses.has(token.address)){
                const wrappedNFTCollection = nftCollections.filter((collection) => collection.wrapped && collection.address == token.address)[0]
                this.graph[token.symbol] = [{
                    token: tokenMap[wrappedNFTCollection.symbol],
                    pool: token.symbol,
                    action: 'Unfractionalize'
                }]
            } else if(!token.wrapped){
                const wrappedToken = {
                    name: 'Wrapped ' + token.symbol,
                    symbol: 'sh' + token.symbol,
                    address: token.address,
                    wrapped: true,
                    oceanID: calculateWrappedTokenId(token.address, 0),
                    icon: ''
                }

                tokenMap[wrappedToken.symbol] = wrappedToken
                
                this.graph[token.symbol] = [{
                    token: wrappedToken,
                    pool: '',
                    action: 'Wrap'
                }]

                this.graph[wrappedToken.symbol] = [{
                    token: tokenMap[token.symbol], 
                    pool: '', 
                    action: 'Unwrap'
                }];

                this.buildWrappedTokenIcon(token).then((icon) => {
                    wrappedToken.icon = icon
                    tokenMap[wrappedToken.symbol] = wrappedToken
                    tokens.push(wrappedToken)
                    tokenLogos[wrappedToken.symbol] = createTokenLogo(wrappedToken, 83)
                })
            }
                
        })

        externalTokens.forEach((token) => {

            const fractionalCollection = nftAddresses.has(token.address)
              ? nftCollections.filter(
                  (collection) =>
                    collection.wrapped && collection.address == token.address
                )[0]
              : null;
            
            const wrappedExternalToken = {
              name: "Wrapped " + token.name,
              symbol: "sh" + token.symbol,
              address: token.address,
              wrapped: true,
              icon: "",
              oceanID:
                token.tokenType == "Shell"
                  ? calculateWrappedTokenId(OLD_OCEAN_ADDRESS, token.oceanID!)
                  : calculateWrappedTokenId(token.address, 0),
              tokenType: token.tokenType,
              protocolVersion: token.protocolVersion,
              pool: fractionalCollection ? fractionalCollection.fractionalizer : ''
            };
  
            tokenMap[wrappedExternalToken.symbol] = wrappedExternalToken
  
            this.graph[token.symbol] = [{
                token: wrappedExternalToken, 
                pool: '', 
                action: 'Wrap'
            }];
  
            this.graph[wrappedExternalToken.symbol] = [{
                token: tokenMap[token.symbol], 
                pool: '', 
                action: 'Unwrap'
            }];

            if(fractionalCollection){
                this.graph[wrappedExternalToken.symbol].push({
                    token: tokenMap[fractionalCollection.symbol],
                    pool: wrappedExternalToken.symbol,
                    action: 'Unfractionalize'
                })
            }
                
        })

        nftCollections.forEach(async (collection) => {
            this.graph[collection.symbol] = [
                {
                    token: tokenMap[collection.wrapped ? collection.symbol.substring(2) : 'sh' + collection.symbol], 
                    pool: '', 
                    action: collection.wrapped ? 'Unwrap' : 'Wrap'
                }
            ];

            if(collection.wrapped && collection.fractionalizer != ''){
                if(collection.is1155){
                    const contractCallContext: ContractCallContext[] = [
                        {
                            reference: 'Fractionalizer',
                            contractAddress: collection.fractionalizer!,
                            abi: Fractionalizer1155ABI as any,
                            calls: Array.from(Array(collection.maxSupply).keys()).map((nftID) => {
                                return {
                                    reference: "fungibleTokenIds",
                                    methodName: "fungibleTokenIds",
                                    methodParameters: [calculateWrappedTokenId(collection.address, BigNumber.from(nftID))]
                                }
                            })
                        }
                    ];

                    // const results : ContractCallResults = await 
                    multicall.call(contractCallContext).then((results : ContractCallResults) => {
                        results.results['Fractionalizer'].callsReturnContext.forEach((callResult, index) => {
                            if(!Zero.eq(callResult.returnValues[0])){

                                const symbol = 'fr' + collection.symbol.substring(2) + '-' + index.toString()

                                const lbpToken = lbpTokens.filter(lbpToken => lbpToken.symbol == symbol)[0]

                                let fungibleToken : any

                                if(lbpToken){
                                    fungibleToken = lbpToken
                                    fungibleToken.oceanID = callResult.returnValues[0]
                                } else {
                                    fungibleToken = {
                                        name: 'Fractional ' + collection.name,
                                        symbol: symbol,
                                        address: collection.address,
                                        wrapped: true,
                                        oceanID: callResult.returnValues[0],
                                        icon: ''
                                    }
                                }

                                this.graph[collection.symbol].push({
                                    token: fungibleToken,
                                    pool: fungibleToken.symbol,
                                    action: 'Fractionalize'
                                })
                                this.graph[fungibleToken.symbol] = [{
                                    token: collection,
                                    pool: fungibleToken.symbol,
                                    action: 'Unfractionalize'
                                }]
                                
                                tokenMap[fungibleToken.symbol] = fungibleToken

                                let lpToken : any = tokenMap[lbpToken.poolName] ?? shellTokens.filter((lpToken) => lpToken.tokens.includes(fungibleToken.symbol))[0] 

                                if(lpToken){

                                    this.graph[lpToken.name] = []

                                    const hasFirstToken = fungibleToken.symbol == lpToken.tokens[0]
                                    const hasSecondToken = fungibleToken.symbol == lpToken.tokens[1]

                                    const childTokenOne = hasFirstToken ? fungibleToken : tokenMap[lpToken.tokens[0]]
                                    const childTokenTwo = hasSecondToken ? fungibleToken : tokenMap[lpToken.tokens[1]]
                                    const childTokenOneID = (hasFirstToken || isShellToken(childTokenOne) || nftAddresses.has(childTokenOne.address) ? '' : 'sh') + getTokenID(childTokenOne)
                                    const childTokenTwoID = (hasSecondToken || isShellToken(childTokenTwo) || nftAddresses.has(childTokenTwo.address) ? '' : 'sh') + getTokenID(childTokenTwo)

                                    this.graph[lpToken.name].push({token: hasFirstToken ? childTokenOne : tokenMap[childTokenOneID], pool: lpToken.name, action: 'Withdraw'})
                                    this.graph[lpToken.name].push({token: hasSecondToken ? childTokenTwo : tokenMap[childTokenTwoID], pool: lpToken.name, action: 'Withdraw'})
                                    this.graph[childTokenOneID].push({token: hasSecondToken ? childTokenTwo : tokenMap[childTokenTwoID], pool: lpToken.name, action: 'Swap'})
                                    this.graph[childTokenOneID].push({token: tokenMap[lpToken.name], pool: lpToken.name, action: 'Deposit'})
                                    this.graph[childTokenTwoID].push({token: hasFirstToken ? childTokenOne : tokenMap[childTokenOneID], pool: lpToken.name, action: 'Swap'})   
                                    this.graph[childTokenTwoID].push({token: tokenMap[lpToken.name], pool: lpToken.name, action: 'Deposit'})
                                }
                            }
                        })
                    })

                } else {
                    const fungibleToken = Object.values(tokenMap).filter(
                      (token) =>
                        token.wrapped &&
                        token.address == collection.address && 
                        token.symbol !== collection.symbol
                    )[0];
                    this.graph[collection.symbol].push({
                        token: tokenMap[fungibleToken.symbol],
                        pool: fungibleToken.symbol,
                        action: 'Fractionalize'
                    })
                }
            }
        })

        externalLPTokens.forEach((lpToken) => {

            const childTokenOne = tokenMap[lpToken.tokens[0]]
            const childTokenTwo = tokenMap[lpToken.tokens[1]]
            if(childTokenOne == undefined || childTokenTwo == undefined) return

            const childTokenOneID = 'sh' + getTokenID(childTokenOne)
            const childTokenTwoID = 'sh' + getTokenID(childTokenTwo)

            if(lpToken.tokenType){

                const wrappedLPToken = {
                    name: "Wrapped " + lpToken.name,
                    symbol: "sh" + lpToken.symbol,
                    address: lpToken.tokenType == "Shell" ? OLD_OCEAN_ADDRESS : lpToken.address,
                    wrapped: true,
                    icon: "",
                    oceanID:
                      lpToken.tokenType == "Shell"
                        ? calculateWrappedTokenId(OLD_OCEAN_ADDRESS, lpToken.oceanID!)
                        : calculateWrappedTokenId(lpToken.address, 0),
                    pool: lpToken.pool,
                    query: lpToken.query,
                    tokenType: lpToken.tokenType,
                    protocolVersion: lpToken.protocolVersion,
                  };
      
                  tokenMap[wrappedLPToken.symbol] = wrappedLPToken
      
                  this.graph[lpToken.symbol] = [{
                      token: wrappedLPToken, 
                      pool: '', 
                      action: 'Wrap'
                  }];
      
                  this.graph[wrappedLPToken.symbol] = [{
                      token: tokenMap[lpToken.symbol], 
                      pool: '', 
                      action: 'Unwrap'
                  }];

                  this.graph[wrappedLPToken.symbol].push({token: tokenMap[childTokenOneID], pool: lpToken.symbol, action: 'Withdraw'})
                  this.graph[wrappedLPToken.symbol].push({token: tokenMap[childTokenTwoID], pool: lpToken.symbol, action: 'Withdraw'})
                  this.graph[childTokenOneID].push({token: tokenMap[wrappedLPToken.symbol], pool: lpToken.symbol, action: 'Deposit'})
                  this.graph[childTokenTwoID].push({token: tokenMap[wrappedLPToken.symbol], pool: lpToken.symbol, action: 'Deposit'})
                  
            }

            this.graph[childTokenOneID].push({token: tokenMap[childTokenTwoID], pool: lpToken.symbol, action: 'Swap'})
            this.graph[childTokenTwoID].push({token: tokenMap[childTokenOneID], pool: lpToken.symbol, action: 'Swap'})   
        })

        shellTokens.forEach((lpToken) => {
            this.graph[lpToken.name] = []

            const childTokenOne = tokenMap[lpToken.tokens[0]]
            const childTokenTwo = tokenMap[lpToken.tokens[1]]
            if(childTokenOne == undefined || childTokenTwo == undefined) return

            const childTokenOneID = (isShellToken(childTokenOne) || nftAddresses.has(childTokenOne.address) ? '' : 'sh') + getTokenID(childTokenOne)
            const childTokenTwoID = (isShellToken(childTokenTwo) || nftAddresses.has(childTokenTwo.address) ? '' : 'sh') + getTokenID(childTokenTwo)

            this.graph[lpToken.name].push({token: tokenMap[childTokenOneID], pool: lpToken.name, action: 'Withdraw'})
            this.graph[lpToken.name].push({token: tokenMap[childTokenTwoID], pool: lpToken.name, action: 'Withdraw'})
            this.graph[childTokenOneID].push({token: tokenMap[childTokenTwoID], pool: lpToken.name, action: 'Swap'})
            this.graph[childTokenOneID].push({token: tokenMap[lpToken.name], pool: lpToken.name, action: 'Deposit'})
            this.graph[childTokenTwoID].push({token: tokenMap[childTokenOneID], pool: lpToken.name, action: 'Swap'})   
            this.graph[childTokenTwoID].push({token: tokenMap[lpToken.name], pool: lpToken.name, action: 'Deposit'})
        })

        const currentTime = Math.floor(Date.now() / 1000)

        lbpTokens.filter(lbpToken => !extract1155Data(lbpToken.symbol)).forEach((lbpToken) => {
            this.graph[lbpToken.symbol] = [{
                token: tokenMap['sh' + lbpToken.symbol], 
                pool: '', 
                action: 'Wrap'
            }];
            
            this.graph[lbpToken.poolName] = []

            const childTokenOneID = 'sh' + lbpToken.symbol
            const childTokenTwoID = 'sh' + lbpToken.pairToken

            this.graph[lbpToken.poolName].push({token: tokenMap[childTokenOneID], pool: lbpToken.poolName, action: 'Withdraw'})
            this.graph[lbpToken.poolName].push({token: tokenMap[childTokenTwoID], pool: lbpToken.poolName, action: 'Withdraw'})
            this.graph[childTokenOneID].push({token: tokenMap[childTokenTwoID], pool: lbpToken.poolName, action: 'Swap'})
            this.graph[childTokenOneID].push({token: tokenMap[lbpToken.poolName], pool: lbpToken.poolName, action: 'Deposit'})
            this.graph[childTokenTwoID].push({token: tokenMap[childTokenOneID], pool: lbpToken.poolName, action: 'Swap'})   
            this.graph[childTokenTwoID].push({token: tokenMap[lbpToken.poolName], pool: lbpToken.poolName, action: 'Deposit'})
        })

        const contractCallContext: ContractCallContext[]  = lbpTokens.map((lbpToken) => {
            return {
                reference: lbpToken.symbol,
                contractAddress: lbpToken.poolAddress,
                abi: LiquidityPoolABI as any,
                calls: [{
                    reference: "implementation",
                    methodName: "implementation",
                    methodParameters: []
                }]
            }
        })

        multicall.call(contractCallContext).then((results : ContractCallResults) => {
            const lbpParamsCallContext = Object.keys(results.results).map((tokenID) => {
                return ({
                    reference: tokenID,
                    contractAddress: results.results[tokenID].callsReturnContext[0].returnValues[0],
                    abi: LBPPoolABI as any,
                    calls: [{
                        reference: "params",
                        methodName: "params",
                        methodParameters: []
                    }]
                })
            })

            multicall.call(lbpParamsCallContext).then((results : ContractCallResults) => {

                for(let index = lbpTokens.length - 1; index >= 0; index--){
                    const lbpToken = lbpTokens[index];
                    if(results.results[lbpToken.symbol].callsReturnContext[0].returnValues.length == 0){ // Pool is no longer an LBP
                        lbpToken.status = 'Ended'
                        //@ts-ignore 
                        shellTokens.push(tokenMap[lbpToken.poolName])
                        if(!extract1155Data(lbpToken.symbol)){
                            tokens.push({
                                name: lbpToken.name,
                                symbol: lbpToken.symbol,
                                address: lbpToken.address,
                                wrapped: false,
                                icon: lbpToken.icon
                            })
                        }
                        lbpTokens.splice(index, 1)
                        continue
                    }
                    const times = results.results[lbpToken.symbol].callsReturnContext[0].returnValues.slice(-3)
                    const [startTime, endTime] = [parseInt(times[0].hex), parseInt(times[1].hex)]
                    
                    if(currentTime > endTime){
                        lbpToken.status = 'Ended'
                        //@ts-ignore 
                        shellTokens.push(tokenMap[lbpToken.poolName])
                        if(!extract1155Data(lbpToken.symbol)){
                            tokens.push({
                                name: lbpToken.name,
                                symbol: lbpToken.symbol,
                                address: lbpToken.address,
                                wrapped: false,
                                icon: lbpToken.icon
                            })
                        }
                        lbpTokens.splice(index, 1)
                    } else if(currentTime > startTime) {
                    lbpToken.status = 'Ongoing'
                    } else {
                        lbpToken.status = 'Upcoming'
                    }
                }
            })
        })

    }

    async buildWrappedTokenIcon(token: any) {
        async function convertPNGtoBase64(url: string): Promise<string> {
            try {
              // Fetch the PNG image from the URL
              const response = await fetch(url);
          
              if (!response.ok) {
                throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
              }
          
              // Convert the image to a blob
              const blob = await response.blob();
          
              // Read the blob as base64 encoding
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  if (typeof reader.result === 'string') {
                    resolve(reader.result);
                  } else {
                    reject(new Error('Failed to read image as base64 encoding'));
                  }
                };
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.error(error);
              throw new Error('Failed to convert PNG to base64 encoding');
            }
        }
    
        const base64String = await convertPNGtoBase64(token.icon)
    
        return `data:image/svg+xml;base64,${Buffer.from(`<?xml version="1.0" encoding="UTF-8"?><svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 799.62 836.92"><defs><style>.cls-1{clip-path:url(#clippath);}.cls-2{fill:none;}.cls-2,.cls-3,.cls-4,.cls-5,.cls-6,.cls-7{stroke-width:0px;}.cls-3{fill:url(#linear-gradient);}.cls-8{clip-path:url(#clippath-1);}.cls-9{clip-path:url(#clippath-4);}.cls-10{clip-path:url(#clippath-3);}.cls-11{clip-path:url(#clippath-2);}.cls-12{clip-path:url(#clippath-6);}.cls-13{clip-path:url(#clippath-5);}.cls-14{opacity:.32;}.cls-15{opacity:.24;}.cls-4{fill:url(#linear-gradient-2);}.cls-5{fill:#151a46;}.cls-6{fill:#4183b5;}.cls-7{fill:#051f3d;}</style><clipPath id="clippath"><path class="cls-2" d="M0,408.82C0,629.54,178.9,808.42,399.6,808.42s399.6-178.9,399.6-399.6S620.3,9.12,399.6,9.12,0,188.12,0,408.82"/></clipPath><linearGradient id="linear-gradient" x1="693.59" y1="724.21" x2="698.14" y2="724.21" gradientTransform="translate(-126828.71 -121841.54) rotate(90) scale(175.68 -175.68)" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#41b9ea"/><stop offset=".03" stop-color="#3eaddc"/><stop offset=".15" stop-color="#3486b0"/><stop offset=".27" stop-color="#2c668b"/><stop offset=".39" stop-color="#264b6c"/><stop offset=".52" stop-color="#203655"/><stop offset=".66" stop-color="#1d2744"/><stop offset=".81" stop-color="#1b1f3a"/><stop offset="1" stop-color="#1a1c37"/></linearGradient><clipPath id="clippath-1"><rect class="cls-2" x="0" y=".12" width="799.5" height="808.3"/></clipPath><clipPath id="clippath-2"><path class="cls-2" d="M799.3,408.82c0,220.7-178.9,399.6-399.6,399.6S0,629.54,0,408.82,178.9,9.12,399.6,9.12s399.7,179,399.7,399.7"/></clipPath><clipPath id="clippath-3"><path class="cls-2" d="M73.1,641.22c1.4,2,2.8,4,4.2,5.9,72.8,97.9,189.3,161.4,320.8,161.4s243-60.8,316.1-155.2c3.1-4,6-8,9-12.1-205.9-96.6-444.1-96.6-650.1,0"/></clipPath><linearGradient id="linear-gradient-2" x1="690.89" y1="747.62" x2="695.43" y2="747.62" gradientTransform="translate(39775.1 37197.61) rotate(-90) scale(52.67 -52.67)" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#1a1c37"/><stop offset=".47" stop-color="#1a1c37"/><stop offset=".68" stop-color="#191b33"/><stop offset=".88" stop-color="#151728"/><stop offset="1" stop-color="#11131d"/></linearGradient><clipPath id="clippath-4"><rect class="cls-2" x="30.1" y="565.12" width="722.3" height="271.8"/></clipPath><clipPath id="clippath-5"><path class="cls-2" d="M397,808.32c128.6,0,243-60.8,316.1-155.2,3.1-4,6.1-8,9-12.1-205.9-96.5-444.1-96.5-650,0,1.4,2,2.8,4,4.2,5.9,72.8,98,189.4,161.4,320.7,161.4"/></clipPath><clipPath id="clippath-6"><rect class="cls-2" x="30.1" y="565.12" width="722.3" height="271.8"/></clipPath></defs><g id="Layer_1-2"><g class="cls-1"><rect class="cls-3" x="0" y="9.12" width="799.3" height="799.3"/></g><g class="cls-15"><g class="cls-8"><path class="cls-5" d="M799.3,408.82c0,220.7-178.9,399.6-399.6,399.6S0,629.54,0,408.82,178.9,9.12,399.6,9.12s399.7,179,399.7,399.7"/><g class="cls-11"><path class="cls-6" d="M400,9.22C179.9,6.72-.3,185.62,0,410.52c.1,49.1,14.5,132.7,41.8,175.2,5.4-.5,116.4-22,86.3-32.3-46.7-15.8,12.5-34.3,4.2-65.8-7.2-27.5-101.8-5.1-105-32.3-1.5-12.8,29.5-20.4,57.3-24.3,48.1-6.7,23.7-39.4,47.7-69.7,19.4-24.6,40.6-30.1,40-66.1-.7-44.3-45.8-28-48.1-60.8-.9-13.1,9.8-11.9,5.7-31.7-3.4-16.6-75-11.8-76.4-12.3,13.1-20.4,29.8-47.7,46.7-65.5,7.7,6.5,36.4,5.9,44.4,32.2,3.7,12.4,12,22.9,17.9,34.3,10.6,20.6,26.7,34,50.2,35.9,11.3.9,34.4.6,44.4-14,14.4-21.1-6.1-21.1-13-37-2.7-6.4.5-11.2,6.7-13.5,7-2.5,15.3.3,18.7,6.9,5.8,11.5,29.1,5.2,25.2-16.5-5.2-29.6-25.8-127.2-11.6-132.9,6.2-2.7,12.6-5.1,19.1-7.1,0,3-3.4,44.1,4,78.2,7.1,33.1,25.5,57.2,39.5,63.9,13.4,6.4,32.4,3,38.5-8.7,8.1-15.6-6.3-30.7-6.3-45.5s13.4-24.5,8.9-42c-4.5-17.9.9-39.1,8.6-56.3.6-1.2,2.2-2.7,3.4-2.7,9.9-.2,19.9-.1,31-.1-2.6,4.8-12.8,24.4-14.3,28.2-9,23.1,8.2,36,1.8,51.7-5.7,13.7-12.5,27.7-5.5,43.4,7.3,16.4,43.1,16.1,36.2-15.2-1.5-7,2.8-13.4,9.5-15.5,11.7-3.7,21.6,1.5,26.1,12.9,5.2,13.2,5.9,27.1,1.4,40.4-4.2,12.3,5.1,22.6,17.2,21.6,28.2-2.2,49.4-16.1,57.5-45,5.8-20.8,11-41.7,9.6-63.6-.1-2.1-.9-13.9-.9-16.5,1.6.4,42.4,21.5,41.5,22.7-20.2,26-6.6,49.4-9.8,65.6-6.3,31.9-23.2,24.7-34.1,37.7-8.1,9.5-8.2,25.2-3.4,31.6,11.9,15.8,39.9,10.1,23.1,32.5-9.8,13.2-3.8,24.9,12.4,29.4,22.4,6.2,44.9,4.9,65.5-4.5,44.5-20.1,60.2-65.6,53.7-92.9-7.1-29.7,41.8,53.5,45,67.1,18.1,76-129.8,42-122.2,109.2,5.3,47.1,52.9,78.9,109.8,87.2,21.8,3.2,23.1,34.5,1.5,38.6-25.3,4.8-74.5-1.2-88.1,16.6-17.2,22.6-5,55.4-29.6,61.9-86.7,22.8,123.2,42.6,123.2,42.6,9.3-16.1,47.6-98.3,42.2-194.4C786.2,169.72,613.1,11.72,400,9.22M90.7,311.02c4.7,3,8.3,7.1,8,13.2-.3,5.9-4.5,8.7-9.2,11-10.5,5-24.1,7.2-30.3,5.7-9.7-2.4-14.2-10-9.6-18.9,7.2-13.7,28.2-19.3,41.1-11M664.6,138.23c-4.6,13.4-22.5,6.5-22.5-7.4,0-9.4,2.4-20.7,18.3-27.1,6.3,5.6,14.5,11.1,21.3,17.2-4.7,5.9-12.8,4.9-17.1,17.3M725.2,351.63c-2.3,21.2-50.6,16.4-61-4.4-3.2-6.4,2.4-12.8,9.2-10.5,19.7,6.7,54.4-9,51.8,14.9"/></g></g></g><g class="cls-10"><rect class="cls-4" x="73.1" y="568.82" width="650" height="239.6"/></g><g class="cls-9"><g class="cls-13"><g class="cls-14"><g class="cls-12"><path class="cls-5" d="M95.5,697.62c4,4.6,7.8,9.3,11.6,14l1.3,1.2c17.1,15.9,33.7,32.3,50.9,48.1,8.4,7.7,17.1,15.3,25.7,22.8.7.6,1.3,1.1,1.8,1.6,2.2,1.5,4.1,2.7,4.2,2.7,7,4.8,14.4,8.6,21.6,13.1.9.6,1.6,1,2.2,1.5,2.3.7,4.3,1.2,4.6,1.4,20,6.7,40.9,9.5,61.6,13,11.6,1.9,23.2,3.8,34.9,5.7l11.4,1.7c-3.9-.6-6.2-1,4.1.6.8.1,1.4.2,1.9.3l1.1.1c.8-.1,1.4-.2,1.8-.3,38.9-7.5,46.6,10.1,86.3,11.6,16.3.6,63-21.2,79.3-21.3,9,0,17.9-.4,26.9-.8,1.7-.1,3-.1,4.2-.2,1.5-.3,3.6-.7,6.5-1.2,5-.9,10.1-1.4,15.1-1.7.3-.2.5-.4.7-.5,10.3-8.2,20.6-16.3,31.2-24.1,10.4-7.5,21.4-14.2,32.8-19.9,11.2-5.8,22.2-11.7,33-18,8.8-5.1,2.8-19.7,11.8-26.8.5-.8,13.7-1.6,17.4-4.8,3.3-2.8,10.5-5.5,13.7-8.4,3.6-5.2,7.5-8.1,10.8-13.5,9.2-15,20.2-25.6,27.7-41.4,1.2-3.2,2.5-6.3,3.8-9.5,3.2-8.2,6.2-16.5,9.1-24.8,2-5.8,4-11.7,5.9-17.6-22.4-3.8-43.9-4.8-60.6-1-31.5,7,47.5,52,11.9,93.4-25.1,29.2-80.4-10.8-113.4,14.2-35.8,27.2-2.2,69.2-49.7,84.5-41.9,13.5-67.6-33.1-146.6-33.7-78.1-.6-105.4,45.5-147.1,31.4-47.2-16-13-57.6-48.4-85.3-28.7-22.4-75.5,5.3-103-8.1h0"/><path class="cls-5" d="M34.5,612.82c.5,1.7,1.2,3.4,1.8,5,7.2,9.2,11.2,24.3,17.5,34.2,1.2,1.9,2.5,3.8,3.7,5.7,1.7,2.4,2.8,3.9,3.4,4.8,2.1,2,4.2,3.9,5.6,5.3,4.4,4.7,8.9,9.2,13.5,13.7-19.5-39.3,48.1-78,18.8-85-18.6-4.5-43.4-3.1-68.7,1.5,1.4,4.9,2.8,9.8,4.4,14.8"/><path class="cls-5" d="M141.9,662.32c14.4,12,63.2-15.5,91.9,6.5,22.7,17.3,8.6,57.7,41.6,69.2,44.6,15.5,61.4-21.2,120-20.7,47.8.4,64,38.5,110.6,22.5,33-11.3,19.7-51.5,42.7-68.5,29.1-21.5,77.4,6.7,92-5,34.4-27.7-73.8-88.9,23.4-83.7-21.9-13.1-76.8-21.1-105.7-5.9-17.5,9.2,40,28.2,24.3,52.8-9.4,14.8-45.9-6.6-74.1,11-24.3,15.1-7.5,42.2-28.1,50.2-17.5,6.8-40-18-89.3-17.6-49.3-1.2-72.1,23.3-89.6,16.2-20.5-8.4-3.2-35.1-27.3-50.7-27.8-18.1-64.6,2.7-73.9-12.3-15.2-24.8,42.5-42.9,25.1-52.4-28.7-15.7-83.6-8.5-105.8,4.3,97.4-3.7-11.7,55.8,22.2,84.1"/></g></g></g></g><path class="cls-7" d="M401.8,630.04c122.2,0,221.3-99.1,221.3-221.3s-99.1-221.3-221.3-221.3-221.3,99.1-221.3,221.3,99.1,221.3,221.3,221.3Z"/></g><image width="256" height="256" transform="translate(190.95 196.71) scale(1.65)" xlink:href="${base64String}"/></svg>`).toString('base64')}`;
    }

    findPath = (startToken: Token | ShellToken, endToken: Token | ShellToken) => {

        const graph = this.adjustGraph([startToken, endToken])
        
        if(isDefaultShellToken(startToken) || isDefaultShellToken(endToken)) return []
    
        const queue: [string, Edge[]][] = [[getTokenID(startToken), [{token: startToken, pool: '', action: ''}]]];
        const visited = new Set();
    
        while (queue.length > 0) {
            let current = queue.shift();
            if (current) {
                let [tokenID, path] = current
                visited.add(tokenID)
                for (let i = 0; i < graph[tokenID].length; i++) {
                    let node = graph[tokenID][i];
                    let nodeID = getTokenID(node.token)
    
                    if (nodeID == getTokenID(endToken)) {
                        path.push(node)
                        return path
                    } else if (!visited.has(nodeID)) {
                        visited.add(nodeID)
                        let newPath = [...path || []]
                        newPath.push(node)
                        queue.push([nodeID, newPath])
                    }
                }
            }
        }
        return []
    }

    isSingleStepPath = (startToken: Token | ShellToken, endToken : Token | ShellToken) => {
        for(const neighbor of this.graph[getTokenID(startToken)]){
            if(getTokenID(neighbor.token) == getTokenID(endToken)){
                return true
            }
        }
        return false
    }

    adjustGraph = (tokens : any[]) => {
        const adjustedGraph = {...this.graph}
        tokens.forEach((token) => {
            if(isNFTCollection(token) && token.is1155){
                const wrappedID = (token.wrapped ? '' : 'sh') + token.symbol 
                const fractionalTokens = adjustedGraph[wrappedID].filter((neighbor) => neighbor.action == 'Fractionalize')
                fractionalTokens.forEach((fractionalToken) => {
                    const fractionalTokenID = fractionalToken.token.symbol
                    if(!fractionalTokenID.includes(token.id1155!.toString())){
                        adjustedGraph[fractionalTokenID].forEach((neighbor) => {
                            const neighborID = getTokenID(neighbor.token)
                            adjustedGraph[neighborID] = adjustedGraph[neighborID]?.filter((neighbor) => fractionalTokenID !== neighbor.token.symbol)
                        })
                        adjustedGraph[fractionalTokenID] = []
                    }
                })
            }
        })
        return adjustedGraph;
    }
}